import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/atoms/Button';
import { Typography } from '@/components/ui/atoms/Typography';
import { EmailField } from '@/components/ui/molecules';
import { useInlineEmailEdit } from '@/hooks/useInlineEmailEdit';
import { cn } from '@/utils/cn';
import type { MouseEvent, PointerEvent } from 'react';

export interface CandidateEmailInlineProps {
  jobId: string;
  candidateId: string;
  hasValidEmail: boolean;
  email?: string | null;
  /** Stop click/pointer from bubbling (table row / kanban card / drag). */
  stopPropagation?: boolean;
  /** Native browser title on truncated email. Default true. */
  showNativeTitle?: boolean;
  className?: string;
}

export function CandidateEmailInline({
  jobId,
  candidateId,
  hasValidEmail,
  email,
  stopPropagation = false,
  showNativeTitle = true,
  className,
}: CandidateEmailInlineProps) {
  const { t } = useTranslation('candidates');
  const {
    showInlineEmail,
    setShowInlineEmail,
    inlineEmailValue,
    setInlineEmailValue,
    handleSave,
    handleCancel,
    isUpdatingEmail,
  } = useInlineEmailEdit({ jobId, candidateId });

  const isolationProps = stopPropagation
    ? {
        onClick: (event: MouseEvent) => event.stopPropagation(),
        onPointerDown: (event: PointerEvent) => event.stopPropagation(),
      }
    : undefined;

  if (hasValidEmail) {
    return (
      <Typography
        variant="body-sm"
        weight="medium"
        color="muted"
        className={cn('truncate', className)}
        title={showNativeTitle ? (email ?? undefined) : undefined}
      >
        {email}
      </Typography>
    );
  }

  if (showInlineEmail) {
    return (
      <div className={cn('min-w-0', className)} {...isolationProps}>
        <EmailField
          value={inlineEmailValue}
          onChange={setInlineEmailValue}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={isUpdatingEmail}
          compact
        />
      </div>
    );
  }

  return (
    <span className={className} {...isolationProps}>
      <Button
        size="xs"
        type="text"
        intent="danger"
        onClick={() => setShowInlineEmail(true)}
        label={t('meta_cv.add_email')}
        icon={<AlertCircle />}
      />
    </span>
  );
}
